import { useState } from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useQuery } from '@tanstack/react-query';
import { GoodsReceiptDetailSchema, GoodsReceiptSchema, type SupplierDto } from '@taqon/contracts';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router';
import { getData, getPage } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { DocumentsPanel } from '../../shared/documents/DocumentsPanel';
import { hasCapability, useSession } from '../../shared/session/session';
import { StatusChip } from '../../shared/ui/forms';
import { ExportButton } from '../../shared/export/ExportButton';
import { exportHref } from '../../shared/export/export-href';
import { SearchFilterBar } from '../../shared/ui/filters';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { PersonName } from '../../shared/ui/person';
import { Pager } from '../crm/common';
import { Facts, RecordFailure, RecordLink } from '../finance/detail';
import { SupplierPicker } from '../finance/lookups';
import { GR_API, GR_TYPE, PO_TYPE, PurchasingLink } from './purchasing';

const PAGE_SIZE = 50;

/**
 * Goods receipts (FRD-PRC-039 to 042, PRC-064): every receipt with its order and supplier, by
 * supplier, date and number. A receipt is recorded from its purchase order's page.
 */
export function GoodsReceiptsPage() {
  const session = useSession();
  const canSeeSuppliers = hasCapability(session.data, 'procurement.supplier:view');
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('search') ?? '');
  const [supplier, setSupplier] = useState<SupplierDto | null>(null);
  const filters = {
    supplier: params.get('supplier') ?? '',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
    search: params.get('search') ?? '',
  };
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const set = (key: keyof typeof filters | 'page', value: string) => {
    const next = new URLSearchParams(params);
    if (value === '') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next);
  };
  const query = new URLSearchParams({
    page: String(page),
    pageSize: String(PAGE_SIZE),
    sort: '-document_no',
  });
  for (const [key, value] of [
    ['supplier_id', filters.supplier],
    ['receipt_date_from', filters.from],
    ['receipt_date_to', filters.to],
    ['search', filters.search],
  ] as const) {
    if (value !== '') query.set(`filter[${key}]`, value);
  }
  const receipts = useQuery({
    queryKey: ['procurement', 'goods-receipts', query.toString()],
    queryFn: () => getPage(`${GR_API}?${query.toString()}`, GoodsReceiptSchema),
  });
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Receipts"
        description="Goods and services received against issued purchase orders."
        actions={
          <ExportButton recordType="procurement.goods_receipt" href={exportHref(GR_API, query)} />
        }
      />
      <SearchFilterBar
        aria-label="Receipt filters"
        label="Receipt or order number"
        value={search}
        onChange={setSearch}
        onSubmit={() => {
          set('search', search.trim());
        }}
      >
        {canSeeSuppliers && (
          <SupplierPicker
            value={supplier}
            required={false}
            onChange={(next) => {
              setSupplier(next);
              set('supplier', next?.id ?? '');
            }}
          />
        )}
        <TextField
          size="small"
          type="date"
          label="Received from"
          value={filters.from}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => {
            set('from', e.target.value);
          }}
        />
        <TextField
          size="small"
          type="date"
          label="Received to"
          value={filters.to}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => {
            set('to', e.target.value);
          }}
        />
      </SearchFilterBar>
      <Section title="Receipts">
        {receipts.isPending && <LoadingState label="Loading receipts" />}
        {receipts.isError && <ErrorState message={receipts.error.message} />}
        {receipts.data?.items.length === 0 && (
          <EmptyState
            title="No receipts"
            detail="A receipt is recorded from an issued purchase order."
          />
        )}
        {receipts.data !== undefined && receipts.data.items.length > 0 && (
          <>
            <Table size="small" aria-label="Receipts">
              <TableHead>
                <TableRow>
                  <TableCell>Receipt</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Purchase order</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Received by</TableCell>
                  <TableCell align="right">Lines</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {receipts.data.items.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <RecordLink to={`/procurement/receipts/${r.id}`}>{r.documentNo}</RecordLink>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.receiptDate}</TableCell>
                    <TableCell>
                      <PurchasingLink type={PO_TYPE} id={r.purchaseOrderId}>
                        {r.purchaseOrderNo}
                      </PurchasingLink>
                    </TableCell>
                    <TableCell>{r.supplierLabel ?? '—'}</TableCell>
                    <TableCell>
                      {r.receivedByLabel == null ? (
                        '—'
                      ) : (
                        <PersonName name={r.receivedByLabel} userId={r.receivedBy} />
                      )}
                    </TableCell>
                    <TableCell align="right">{r.lineCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pager
              page={page}
              pageSize={PAGE_SIZE}
              total={receipts.data.total}
              noun="receipts"
              onPage={(next) => {
                set('page', String(next));
              }}
            />
          </>
        )}
      </Section>
    </Stack>
  );
}

/** One receipt (`/procurement/receipts/:id`): its lines, order and supplier (FRD-PRC-041). */
export function GoodsReceiptDetailPage() {
  const { id = '' } = useParams();
  const session = useSession();
  const receipt = useQuery({
    queryKey: ['procurement', 'goods-receipt', id],
    queryFn: () => getData(`${GR_API}/${id}`, GoodsReceiptDetailSchema),
  });
  const back = (
    <Button component={RouterLink} to="/procurement/receipts" startIcon={<ArrowBackIcon />}>
      All receipts
    </Button>
  );
  if (receipt.isPending) return <LoadingState label="Loading the receipt" />;
  if (receipt.isError) {
    return <RecordFailure title="Receipt" noun="receipt" error={receipt.error} back={back} />;
  }
  const r = receipt.data.receipt;
  return (
    <Stack spacing={3}>
      <PageHeader title={`Receipt ${r.documentNo}`} actions={back} />
      <Section title="Receipt">
        <Facts
          label="Receipt details"
          facts={[
            ['Status', <StatusChip key="s" status={r.status} />],
            ['Receipt date', r.receiptDate],
            [
              'Received by',
              r.receivedByLabel == null ? (
                r.receivedBy
              ) : (
                <PersonName key="rb" name={r.receivedByLabel} userId={r.receivedBy} />
              ),
            ],
            [
              'Purchase order',
              <PurchasingLink key="po" type={PO_TYPE} id={r.purchaseOrderId}>
                {r.purchaseOrderNo}
              </PurchasingLink>,
            ],
            ['Order status', <StatusChip key="os" status={receipt.data.purchaseOrderStatus} />],
            [
              'Supplier',
              <PurchasingLink key="sup" type="procurement.supplier" id={r.supplierId}>
                {r.supplierLabel ?? r.supplierId}
              </PurchasingLink>,
            ],
          ]}
        />
      </Section>
      <Section title="Lines">
        <Table size="small" aria-label="Receipt lines">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Order line</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Quantity received</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {receipt.data.lines.map((line) => (
              <TableRow key={line.id}>
                <TableCell>{line.lineNo}</TableCell>
                <TableCell>{line.purchaseOrderLineNo}</TableCell>
                <TableCell>{line.description}</TableCell>
                <TableCell align="right">{line.quantityReceived}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
      <DocumentsPanel
        recordType={GR_TYPE}
        recordId={r.id}
        canAttach={hasCapability(session.data, `${GR_TYPE}:create`)}
      />
      <RecordHistory recordType={GR_TYPE} recordId={r.id} />
    </Stack>
  );
}
